// Sentry
import * as Sentry from '@sentry/react'

import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { API_WALLET_URL } from '../../config'
import storage from '../../utils/storage'
import { storageKey } from '../../constants/storage'

axios.defaults.baseURL = API_WALLET_URL
axios.defaults.headers['Content-Type'] = 'application/json'
axios.defaults.validateStatus = () => true
axios.defaults.withCredentials = false

export type ApiResponse<T> = {
  success: boolean
  statusCode?: number
  data?: T
  error?: any
}

type ApiCallOptions = AxiosRequestConfig & {
  headers?: Record<string, string>
  [x: string]: unknown
}

export const apiCall = async <T>(
  url: string,
  options: ApiCallOptions
): Promise<ApiResponse<T>> => {
  const user = storage.get(storageKey.USER)

  const transactionId = Math.random().toString(36).slice(2, 9)
  Sentry.configureScope((scope) => {
    scope.setTag('transaction_id', transactionId)
  })

  try {
    const res: AxiosResponse = await axios(url, {
      ...options,
      withCredentials: false,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${user?.access_token}`,
        'Transaction-ID': transactionId
      }
    })

    const result: ApiResponse<T> =
      res.status >= 300
        ? { success: false, statusCode: res.status, error: res.data }
        : { success: true, statusCode: res.status, data: res.data }

    return result
  } catch (err) {
    if (!axios.isCancel(err)) {
      throw err
    }
    return { success: false, error: 'Request was cancelled' }
  }
}

export const addResponseInterceptor = (fn, errFn) =>
  axios.interceptors.response.use(fn, errFn)

export const ejectResponseInterceptor = (interceptor) => {
  axios.interceptors.response.eject(interceptor)
}
